import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { JournalFamily, JournalPage } from '../lib/types';
import { Bar, EmptyState, ErrorState, LoadingGrid, Panel } from '../components/ui';
import { EggArt } from '../components/EggArt';
import { dexNumber, formatDuration, titleCase } from '../lib/format';

/**
 * The Journal is the answer to "which egg should I buy".
 *
 * Nothing here is given: a family stays nameless until you have held one, and a
 * species only appears once *you* have hatched it from *that* family. That is
 * what makes the knowledge worth trading — the server never sends a species you
 * haven't earned, so there is nothing to read out of devtools either.
 */
export function JournalPageIndex() {
  const state = useAsync<{ families: JournalFamily[] }>(() => api.get('/eggs/journal'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const families = state.data?.families ?? [];
  const known = families.filter((family) => family.known);
  const found = families.reduce((sum, family) => sum + family.discovered, 0);
  const total = families.reduce((sum, family) => sum + family.totalSpecies, 0);

  const themes = [...new Set(families.map((family) => family.theme))];

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Discovery Journal</h1>
          <div className="page-head__sub">
            A species can belong to many families at very different odds. Everything below is what
            you have hatched yourself.
          </div>
        </div>
        <Link className="btn btn--primary" to="/eggs">
          Egg Market
        </Link>
      </div>

      <div className="pxframe rotation-bar">
        <div className="stack-sm" style={{ gap: 2, minWidth: 130 }}>
          <div className="field__label">Families held</div>
          <div className="rotation-clock">
            {known.length} / {families.length}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <Bar ratio={found / Math.max(1, total)} variant="egg" />
          <div className="tiny muted" style={{ marginTop: 4 }}>
            {found.toLocaleString()} of {total.toLocaleString()} entries recorded
          </div>
        </div>
      </div>

      {state.loading && !state.data ? (
        <LoadingGrid count={12} height={92} />
      ) : (
        themes.map((theme) => (
          <Panel key={theme} title={titleCase(theme)}>
            <div className="journal-grid">
              {families
                .filter((family) => family.theme === theme)
                .map((family) => (
                  <FamilyCard key={family.key} family={family} />
                ))}
            </div>
          </Panel>
        ))
      )}
    </div>
  );
}

function FamilyCard({ family }: { family: JournalFamily }) {
  const inner = (
    <>
      <EggArt appearance={family.appearance} size="sm" state="still" label={family.name} />
      <div className="stack-sm" style={{ flex: 1, gap: 4, minWidth: 0 }}>
        <div className="mono tiny">{family.name}</div>
        {family.known ? (
          <>
            <Bar ratio={family.discovered / Math.max(1, family.totalSpecies)} variant="egg" />
            <div className="tiny muted">
              {family.discovered} / {family.totalSpecies} found
            </div>
          </>
        ) : (
          <div className="tiny muted">Never held</div>
        )}
      </div>
    </>
  );

  if (!family.known) {
    return <div className="pxframe journal-card journal-card--unknown">{inner}</div>;
  }
  return (
    <Link className="pxframe journal-card" to={`/journal/${family.key}`}>
      {inner}
    </Link>
  );
}

/* --- One family's page ----------------------------------------------------- */

export function JournalFamilyPage() {
  const { key = '' } = useParams();
  const state = useAsync<JournalPage>(() => api.get(`/eggs/journal/${key}`), [key]);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <LoadingGrid count={8} height={110} />;

  const { family, entries, found, total } = state.data;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <Link className="tiny muted" to="/journal">
            ← Journal
          </Link>
          <h1 className="page-head__title">{family.name}</h1>
          <div className="page-head__sub">{family.description}</div>
        </div>
        <Link className="btn btn--primary" to="/eggs">
          Egg Market
        </Link>
      </div>

      <div className="pxframe rotation-bar">
        <EggArt appearance={family.appearance} label={family.name} />
        <div className="stack-sm" style={{ flex: 1, minWidth: 180 }}>
          <div className="row row--between tiny mono">
            <span>{titleCase(family.theme)}</span>
            <span className="muted">
              {found} / {total} found
            </span>
          </div>
          <Bar ratio={found / Math.max(1, total)} variant="egg" />
          <div className="tiny muted">~{formatDuration(family.hatchSteps * 45)} to hatch</div>
        </div>
      </div>

      <Panel title="◔ What has come out of these" hint={`${total - found} still unknown`}>
        {total === 0 ? (
          <EmptyState art="◌" title="Nothing lives in this family yet" />
        ) : (
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(94px, 1fr))', gap: 6 }}
          >
            {entries.map((entry, index) =>
              entry.discovered && entry.speciesId != null ? (
                <Link
                  key={entry.speciesId}
                  to={`/dex/${entry.speciesId}`}
                  className="pxwell journal-slot"
                >
                  <img
                    className="pixel"
                    src={entry.sprite ?? ''}
                    alt=""
                    style={{ width: 48, height: 48 }}
                    loading="lazy"
                  />
                  <span className="journal-slot__name">{entry.name}</span>
                  <span className="tiny muted mono">{dexNumber(entry.speciesId)}</span>
                </Link>
              ) : (
                <div key={`blank-${index}`} className="pxwell journal-slot journal-slot--blank">
                  <span className="journal-slot__blank" aria-hidden>
                    ?
                  </span>
                  <span className="journal-slot__name">???</span>
                </div>
              ),
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
